import { getReadableShiftCodeTextColor } from "@/utils/fontSizeUtils";
import { TableRow } from "@/components/ui/table";
import { TableCell } from "@/components/ui/table";
import { DateUtils, getWeekdayLabel, type Weekday } from "@/utils/dayUtill";
import { Input } from "@/components/ui/input";
import {
  ChevronDown,
  ChevronUp,
  PencilLine,
  Trash2,
  GripVertical,
} from "lucide-react";
import type {
  NurseResponse,
  NurseWithRelation,
  UnionShift,
} from "@/types/nurse-type";
import {
  Controller,
  useForm,
  FormProvider,
  type FieldErrors,
} from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { sortShiftArray } from "@/utils/nurse-util";
import { resolveGradeLabel } from "@/utils/grade-util";
import { useIsAdmin, useIsGroupNurse } from "@/hooks/useAuth";
import { toast } from "@/components/toast/CustomToast";
import StyledDateInput from "@/components/common/StyledDateInput";
import MemoCell from "@/components/feature/head_nurse_mgmt/MemoCell";
import type { GradeConfigQueryResult } from "@/hooks/useGradeConfig";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { ShiftPopover } from "./ShiftPopover";
import { NurseBulkSchema } from "../schema/nurseBulkSchema";
import useNurseMutation from "../hooks/useNurseMutation";
import { useSaveNurseMonthlyLimits } from "../hooks/useNurseMonthlyLimits";
import { usePatchNurseRow } from "../hooks/usePatchNurseRow";
import { cn } from "@/utils/cn";
import dayjs from "dayjs";
import httpClient from "@/config/axios";
import DeleteConfirmModal from "./DeleteConfirmModal";
import { images } from "@/assets/img/imagesMap";
import CustomModal from "@/components/modal/CustomModal";
import { MonthlyMembershipBadge } from "./MonthlyMembershipContext";
import { useMonthlyMembership } from "@/components/feature/nurse-membership/monthlyMembershipStore";
import LoadingSpiner from "@/components/ui/LoadingSpiner";
import { formatExperienceYears } from "@/utils/experience";

type NurseBulkFormValues = z.infer<typeof NurseBulkSchema>;
type ShiftOption = { label: string; value: string; color?: string };

type NightLimitMode = "fixed" | "max" | "none";

function patchValuesEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) =>
      patchValuesEqual(value, sortedB[index])
    );
  }
  return false;
}

function buildNurseRowPatchBody(
  previous: NurseResponse,
  next: NurseBulkFormValues,
  formatDate: (date: Date | null | string) => string
): Record<string, unknown> {
  const previousPatch: Record<string, unknown> = {
    role: previous.role || "",
    level_: previous.level_ || "",
    grade: previous.grade ?? null,
    experience: previous.experience ?? 0,
    is_head_nurse: previous.is_head_nurse,
    weekly_off_weekday: previous.weekly_off_weekday,
    allowed_shifts: sortShiftArray(previous.allowed_shifts ?? []),
    exclusion_partner_id: previous.exclusion_partner_id ?? null,
    joining_date: formatDate(previous.joining_date) || null,
    resignation_date: previous.resignation_date
      ? formatDate(previous.resignation_date)
      : null,
    nurse_memo: previous.nurse_memo,
    is_weekend_off: previous.is_weekend_off ?? false,
    fixed_shift: previous.fixed_shift ?? null,
    enable_aide: previous.enable_aide ?? false,
    enable_nurse_pair_preference:
      previous.enable_nurse_pair_preference ?? false,
    wanted_max_requests: previous.wanted_max_requests ?? null,
  };

  const nextPatch: Record<string, unknown> = {
    role: next.role || "",
    level_: next.level_ || "",
    grade: next.grade && next.grade !== 0 ? next.grade : null,
    experience: next.experience ?? 0,
    is_head_nurse: next.is_head_nurse,
    weekly_off_weekday: next.weekly_off_weekday,
    allowed_shifts: sortShiftArray(next.allowed_shifts ?? []),
    exclusion_partner_id: next.exclusion_partner_id ?? null,
    joining_date: next.joining_date || null,
    resignation_date: next.resignation_date || null,
    nurse_memo: next.nurse_memo,
    is_weekend_off: next.is_weekend_off ?? false,
    fixed_shift: next.fixed_shift ?? null,
    enable_aide: next.enable_aide,
    enable_nurse_pair_preference: next.enable_nurse_pair_preference,
    wanted_max_requests: next.wanted_max_requests ?? null,
  };

  const diff: Record<string, unknown> = {};
  Object.entries(nextPatch).forEach(([key, value]) => {
    if (!patchValuesEqual(previousPatch[key], value)) {
      diff[key] = value;
    }
  });

  return diff;
}

function NurseTableRowComponent({
  nurse,
  idx,
  selectedGroupId,
  activeForSideProfile,
  selectIdForSideprofile,
  enableDragHandle = false,
  disableEdit = false,
  onCancelNewNurse,
  hideEditColumn = false,
  visibleColumns,
  enableSelection = false,
  isSelected = false,
  onToggleSelection,
  addNurseModal = false,
  year,
  month,
  nurseCacheData,
  gradeData,
  shiftOptions,
  fixedShiftOptions,
  shiftColorMap,
  isUseMid = false,
  groupName,
  onMoveUp,
  onMoveDown,
  canMoveUp = false,
  canMoveDown = false,
  showReorder = false,
  dispatchGroupTopBorder = false,
}: {
  nurse: NurseWithRelation;
  idx: number;
  selectedGroupId: string | null;
  activeForSideProfile: boolean;
  selectIdForSideprofile?: (bool: boolean, nurseID: string) => void;
  enableDragHandle?: boolean;
  disableEdit?: boolean;
  onCancelNewNurse?: () => void;
  hideEditColumn?: boolean;
  visibleColumns?: string[];

  enableSelection?: boolean;
  isSelected?: boolean;
  onToggleSelection?: (nurseId: string) => void;
  addNurseModal?: boolean;
  // 근무자관리 월 셀렉터 기준 연/월. 주어질 때만 나이트 개수 인라인 편집 활성화
  // (저장 대상은 nurse_monthly_limits.n_exact, scope=nurse_id+group_id+year+month)
  year?: number;
  month?: number;
  nurseCacheData?: NurseResponse[];
  gradeData?: GradeConfigQueryResult;
  shiftOptions: ShiftOption[];
  fixedShiftOptions: ShiftOption[];
  shiftColorMap: Map<string, string>;
  isUseMid?: boolean;
  groupName?: string;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  canMoveUp?: boolean;
  canMoveDown?: boolean;
  showReorder?: boolean;
  dispatchGroupTopBorder?: boolean;
}) {
  const formattedBirthDate = nurse.birth_date
    ? dayjs(nurse.birth_date).format("YYYY-MM-DD")
    : "-";

  const [isEditMode, setIsEditMode] = useState(
    nurse.nurse_id.startsWith("temp_")
  );

  const [isNightWeekendBlockedModalOpen, setIsNightWeekendBlockeModalOpen] =
    useState<boolean>(false);

  const isAdmin = useIsAdmin();
  const isGroupNurse = useIsGroupNurse();
  const monthlyMembership = useMonthlyMembership(nurse.nurse_id);
  const nightFixedCount = nurse.n_exact ?? null;
  // 나이트 최대(n_max) — 고정이 없을 때만 "≤N개"로 표시(일괄 최대 저장분 노출).
  const nightMaxCount = nurse.n_max ?? null;

  // 나이트 개수 인라인 편집 — 월(year/month)과 nurse.group_id 가 있어야 저장 가능.
  //   행별로 고정(n_exact)/최대(n_max) 중 하나를 선택해 저장(상호배타).
  const NIGHT_LIMIT_MAX = 15;
  const nightLimitGroupId = selectedGroupId || nurse.group_id || null;
  const nurseListQueryGroupId = isAdmin ? (selectedGroupId ?? "") : "";
  const canEditNight =
    year != null && month != null && !!nightLimitGroupId && !addNurseModal;
  const initialNightMode: NightLimitMode =
    nightFixedCount != null
      ? "fixed"
      : nightMaxCount != null
        ? "max"
        : "none";
  const initialNightValue = nightFixedCount ?? nightMaxCount ?? null;
  const [nightMode, setNightMode] = useState<NightLimitMode>(initialNightMode);
  const [nightDraft, setNightDraft] = useState<number | null>(initialNightValue);
  const [isNightSettingsOpen, setIsNightSettingsOpen] = useState(false);
  const nightDirty =
    canEditNight &&
    (nightDraft !== initialNightValue || nightMode !== initialNightMode);

  const nightSettingLabel =
    nightMode === "fixed" && nightDraft != null
      ? `고정 ${nightDraft}개`
      : nightMode === "max" && nightDraft != null
        ? `최대 ${nightDraft}개`
        : "설정 안 함";

  const groupId = isAdmin ? selectedGroupId : "";
  const memberGroupId = selectedGroupId || nurse.group_id || groupId;
  const fullSchemaGroupId = isAdmin ? selectedGroupId : undefined;

  const queryClient = useQueryClient();
  const get = nurseCacheData;
  const exclusionPartnerName = useMemo(() => {
    if (!nurse.exclusion_partner_id) return null;
    return (
      get?.find((item) => item.nurse_id === nurse.exclusion_partner_id)?.name ??
      nurse.exclusion_partner_id
    );
  }, [get, nurse.exclusion_partner_id]);

  // 개인 프리셉터 관련 데이터
  const rowRef = useRef<HTMLTableRowElement>(null);

  function filterDate(date: Date | null | string) {
    if (!date) return "";
    return DateUtils.parseKoreanDate(date).format("YYYY-MM-DD");
  }

  const { preceptor, preceptees, ...pureNurse } = nurse;
  void preceptor;
  void preceptees;

  const createUtcDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  };

  const methods = useForm<NurseBulkFormValues>({
    resolver: zodResolver(NurseBulkSchema),
    values: {
      role: nurse.role,
      level_: nurse.level_ || "",
      grade: nurse.grade,
      experience: nurse.experience,
      is_head_nurse: nurse.is_head_nurse,
      weekly_off_weekday: nurse.weekly_off_weekday,
      allowed_shifts: nurse.allowed_shifts ?? [],
      preceptor_id: nurse.preceptor_id,
      exclusion_partner_id: nurse.exclusion_partner_id ?? null,
      joining_date: filterDate(nurse.joining_date),
      resignation_date: filterDate(nurse.resignation_date),
      nurse_memo: nurse.nurse_memo,
      is_weekend_off: nurse.is_weekend_off ?? false,
      fixed_shift: nurse.fixed_shift ?? null,
      enable_aide: nurse.enable_aide ?? false,
      enable_nurse_pair_preference: nurse.enable_nurse_pair_preference ?? false,
      wanted_max_requests: nurse.wanted_max_requests ?? null,
    },
  });

  const {
    reset,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { isDirty },
  } = methods;

  useEffect(() => {
    if (isEditMode) {
      // 수정 모드로 들어갈 때 폼 데이터 재설정
      reset({
        role: nurse.role,
        level_: nurse.level_ || "",
        grade: nurse.grade,
        experience: nurse.experience,
        is_head_nurse: nurse.is_head_nurse,
        weekly_off_weekday: nurse.weekly_off_weekday,
        allowed_shifts: nurse.allowed_shifts ?? [],
        preceptor_id: nurse.preceptor_id,
        exclusion_partner_id: nurse.exclusion_partner_id ?? null,
        joining_date: filterDate(nurse.joining_date),
        resignation_date: filterDate(nurse.resignation_date),
        nurse_memo: nurse.nurse_memo,
        is_weekend_off: nurse.is_weekend_off ?? false,
        fixed_shift: nurse.fixed_shift ?? null, // 이 값이 제대로 들어가는지 확인
        enable_aide: nurse.enable_aide ?? false,
        enable_nurse_pair_preference:
          nurse.enable_nurse_pair_preference ?? false,
        wanted_max_requests: nurse.wanted_max_requests ?? null,
      });
    }
  }, [isEditMode, nurse, reset]);

  const watchedGrade = watch("grade");

  /** `grade_names` 기준 옵션 + 폼 값이 목록에 없을 때(레거시 등) 1행 보정 */
  const gradeSelectOptions = useMemo(() => {
    const src = gradeData?.gradeSelectOptions ?? [];
    const base = src.length > 0 ? [...src] : [];
    const g =
      watchedGrade != null && watchedGrade !== 0 ? Number(watchedGrade) : null;
    if (g != null && !base.some((o) => o.value === String(g))) {
      base.push({
        value: String(g),
        label: resolveGradeLabel(g, src, gradeData?.grade_names ?? undefined),
      });
    }
    return base;
  }, [gradeData?.gradeSelectOptions, gradeData?.grade_names, watchedGrade]);

  const { mutate: nurseMutation, isPending: isBulkSavePending } =
    useNurseMutation();
  const { mutate: patchNurseRow, isPending: isRowSavePending } =
    usePatchNurseRow();

  // 나이트 개수 저장(PUT /nurses/monthly-limits). scope=nurse.group_id+year+month.
  const { mutate: saveNightLimit, isPending: isNightSavePending } =
    useSaveNurseMonthlyLimits({
      year: year ?? 0,
      month: month ?? 0,
      groupId: nightLimitGroupId,
      listGroupId: nurseListQueryGroupId,
      nurseId: nurse.nurse_id,
      showToast: false,
    });

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { mutate: deleteNurseMutation, isPending: isDeletePending } =
    useMutation({
      mutationFn: async (id: string) => {
        await httpClient.delete(`/nurses/${id}`);
      },
      onSuccess: () => {
        toast.success("삭제되었습니다.");
        setIsEditMode(false);
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.listScope(groupId),
        });
        // 월 소속 명단(group_members_in_month)도 무효화. 안 하면 stale 캐시의
        // 멤버가 NurseManagementTables 의 합성(synthInbound) 행으로 되살아나
        // 삭제된 근무자가 목록에 그대로 남는다(새로고침 전까지).
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.membersScope(memberGroupId),
        });
        // 사이드프로필 캐싱 무효화
        queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.nurses.detail(nurse.nurse_id),
          refetchType: "all",
        });
        setIsDeleteModalOpen(false);
      },
    });

  const isPending =
    isBulkSavePending ||
    isRowSavePending ||
    isDeletePending ||
    isNightSavePending;
  const isRowMutationPending =
    isBulkSavePending || isRowSavePending || isNightSavePending;

  useEffect(() => {
    setNightDraft(nightFixedCount ?? nightMaxCount ?? null);
    setNightMode(
      nightFixedCount != null
        ? "fixed"
        : nightMaxCount != null
          ? "max"
          : "none"
    );
  }, [isEditMode, nightFixedCount, nightMaxCount]);

  // nurse prop이 변경되면 폼 데이터를 최신화 (수정 모드가 아닐 때만)
  useEffect(() => {
    if (!isEditMode) {
      reset({
        role: nurse.role,
        level_: nurse.level_ || "",
        grade: nurse.grade,
        experience: nurse.experience,
        is_head_nurse: nurse.is_head_nurse,
        weekly_off_weekday: nurse.weekly_off_weekday,
        allowed_shifts: nurse.allowed_shifts ?? [],
        preceptor_id: nurse.preceptor_id,
        exclusion_partner_id: nurse.exclusion_partner_id ?? null,
        joining_date: filterDate(nurse.joining_date),
        resignation_date: filterDate(nurse.resignation_date),
        nurse_memo: nurse.nurse_memo,
        is_weekend_off: nurse.is_weekend_off ?? false,
        enable_aide: nurse.enable_aide ?? false,
        enable_nurse_pair_preference:
          nurse.enable_nurse_pair_preference ?? false,
        wanted_max_requests: nurse.wanted_max_requests ?? null,
      });
    }
  }, [nurse, reset, isEditMode]);

  // 주말휴무가 "미적용"으로 변경되면 고정근무 제거
  const isWeekendOff = watch("is_weekend_off");
  useEffect(() => {
    if (isEditMode && isWeekendOff === false) {
      setValue("fixed_shift", null);
    }
  }, [isWeekendOff, isEditMode, setValue]);

  // 외부 클릭 시 취소 확인 핸들러
  const handleClickOutside = useCallback(
    (event: PointerEvent) => {
      if (
        !rowRef.current ||
        !isEditMode ||
        isPending ||
        isNightWeekendBlockedModalOpen
      )
        return;

      const target = event.target as Node | null;
      if (!target) return;

      // Radix UI 포털 내부 클릭이거나, DOM에서 제거된 경우는 외부 클릭으로 간주하지 않음
      if (
        target instanceof Element &&
        (target.closest("[data-radix-portal]") ||
          target.closest("[data-radix-popper-content-wrapper]") ||
          target.closest("[role='listbox']") ||
          target.closest("[role='option']") ||
          target.closest("[role='dialog']") ||
          !document.body.contains(target))
      ) {
        return;
      }

      // 현재 행 내부 클릭이면 무시
      if (rowRef.current.contains(target)) return;

      // 외부 클릭 시 취소 확인
      if (isDirty) {
        if (confirm("수정된 내용이 있습니다.\n취소하시겠습니까?")) {
          reset();
          setIsEditMode(false);
        }
      } else {
        // temp 간호사인 경우 외부 클릭 시 삭제하거나 그대로 둘지 결정 필요.
        // 현재 로직상으론 그냥 수정 모드만 꺼짐.
        setIsEditMode(false);
      }
    },
    [isEditMode, isDirty, isPending, reset, isNightWeekendBlockedModalOpen]
  );

  useEffect(() => {
    document.addEventListener("pointerdown", handleClickOutside, true);
    return () => {
      document.removeEventListener("pointerdown", handleClickOutside, true);
    };
  }, [handleClickOutside]);

  const commitNight = (done: () => void) => {
    if (!nightDirty || year == null || month == null || !nightLimitGroupId) {
      done();
      return;
    }
    if (nightMode !== "none" && nightDraft == null) {
      toast.error("고정 또는 최대 횟수를 입력해주세요.");
      return;
    }
    saveNightLimit(
      {
        year,
        month,
        limits: [
          {
            nurse_id: nurse.nurse_id,
            group_id: nightLimitGroupId,
            year,
            month,
            ...(nightMode === "fixed"
              ? { n_exact: nightDraft }
              : nightMode === "max"
                ? { n_max: nightDraft }
                : { n_exact: null, n_max: null }),
          },
        ],
      },
      {
        onSuccess: () => done(),
        onError: (err: Error) => {
          toast.error(err?.message || "나이트 개수 저장에 실패했습니다.");
          done();
        },
      }
    );
  };

  const onSubmit = (data: NurseBulkFormValues) => {
    if (!isDirty && !nightDirty) {
      setIsEditMode(false);
      return;
    }

    // 나이트 개수만 변경된 경우: 간호사 bulk 저장 없이 월 한도만 저장.
    // (validator 통과 후 성공 toast 는 노출하지 않음)
    if (!isDirty) {
      commitNight(() => {
        setIsEditMode(false);
      });
      return;
    }

    if (!get) {
      toast.error("데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    // 근무형태가 'N' 이고 주말 휴무가 '적용' 인 경우
    if (
      data.allowed_shifts.length === 1 &&
      data.allowed_shifts.includes("N") &&
      data.is_weekend_off
    ) {
      setIsNightWeekendBlockeModalOpen(true);
      return;
    }

    const joiningDate = createUtcDate(data.joining_date);
    const resignationDate = createUtcDate(data.resignation_date);

    const mergedData: NurseResponse = {
      ...pureNurse,
      ...data,
      role: data.role || "",
      level_: data.level_ || "",
      grade: data.grade && data.grade !== 0 ? data.grade : null,
      allowed_shifts: sortShiftArray(data.allowed_shifts) as UnionShift[],
      joining_date: joiningDate,
      resignation_date: resignationDate,
      nurse_memo: data.nurse_memo,
      is_weekend_off: data.is_weekend_off ?? false,
      fixed_shift: data.fixed_shift ?? null,
      wanted_max_requests: data.wanted_max_requests ?? null,
    } as NurseResponse;

    const updatedList = get.map((n) =>
      n.nurse_id === nurse.nurse_id ? mergedData : n
    );

    // 그룹 관리자 또는 그룹 간호사가 아닐 때만 수간호사 최소 1명 검증
    if (!isAdmin && !isGroupNurse) {
      const hasHeadNurse = updatedList.some(
        (n) => n.active === 1 && n.is_head_nurse === true
      );

      if (!hasHeadNurse) {
        toast.error("수간호사는 최소 1명이어야 합니다.");
        return;
      }
    }

    if (nurse.nurse_id.startsWith("temp_")) {
      nurseMutation(updatedList, {
      onSuccess: () => {
        // 나이트 개수가 함께 변경됐으면 이어서 저장(월 한도 테이블).
        commitNight(() => {
          setIsEditMode(false);
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.nurses.listScope(groupId),
          });
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.nurses.membersScope(memberGroupId),
          });
          // 사이드프로필 캐싱 무효화
          queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.nurses.detail(nurse.nurse_id),
            refetchType: "all",
          });
        });
      },
    });
      return;
    }

    const patchBody = buildNurseRowPatchBody(pureNurse, data, filterDate);

    if (Object.keys(patchBody).length === 0) {
      commitNight(() => {
        setIsEditMode(false);
      });
      return;
    }

    patchNurseRow(
      {
        nurseId: nurse.nurse_id,
        nurseName: nurse.name,
        patchBody,
        optimisticNurse: mergedData,
        listGroupId: nurseListQueryGroupId,
        memberGroupId,
        fullSchemaGroupId,
      },
      {
        onSuccess: () => {
          commitNight(() => {
            setIsEditMode(false);
          });
        },
      }
    );
  };

  // 근무자 삭제
  const onDeleteHandler = useCallback(() => {
    if (nurse.nurse_id.startsWith("temp_")) {
      if (onCancelNewNurse) onCancelNewNurse();
      return;
    }
    setIsDeleteModalOpen(true);
  }, [nurse.nurse_id, onCancelNewNurse]);

  const onError = (errors: FieldErrors<NurseBulkFormValues>) => {
    const errorList = Object.values(errors);
    if (errorList.length > 0) {
      const firstError = errorList[0];
      if (firstError?.message) {
        toast.error(firstError.message);
      }
    }
  };

  // preceptees가 한명이라도 있으면 프리셉터
  const isPreceptor = (nurse.preceptees?.length ?? 0) > 0;

  const isDisabledNurse =
    !!nurse.is_head_nurse && nurse.role === "RN" && !!isAdmin;
  const isExternalNurseInCurrentGroup =
    monthlyMembership?.membership_status === "inbound" ||
    (!!selectedGroupId && nurse.group_id !== selectedGroupId);

  useEffect(() => {
    if (isExternalNurseInCurrentGroup && isEditMode) {
      setIsEditMode(false);
    }
  }, [isExternalNurseInCurrentGroup, isEditMode]);

  return (
    <FormProvider {...methods}>
      <TableRow
        // disabled={isDisabledNurse}
        key={idx}
        ref={rowRef}
        className={cn(
          "bg-white group transition-colors",
          (isEditMode || isRowMutationPending) &&
            "z-10 bg-blue-50/50 relative",
          activeForSideProfile ? "bg-blue-50 hover:bg-blue-100" : "",
          enableDragHandle && "cursor-grab active:cursor-grabbing",
          enableSelection && "cursor-pointer hover:bg-gray-50",
          enableSelection && isSelected && "!bg-[#F0F0F2]",
          dispatchGroupTopBorder &&
            "[&>td]:border-t [&>td]:border-[#D6D6DE]"
        )}
        style={
          isEditMode || isRowMutationPending
            ? { position: "relative" }
            : undefined
        }
        data-nid={nurse.nurse_id}
        data-nurse-row-id={nurse.nurse_id}
        onClick={(e) => {
          // 체크박스 클릭은 이벤트 버블링으로 처리되므로 여기서는 체크박스가 아닐 때만 토글
          const target = e.target as HTMLElement;
          if (
            enableSelection &&
            onToggleSelection &&
            !isEditMode &&
            !target.closest('input[type="checkbox"]') // 체크박스 클릭이 아닐 때만
          ) {
            onToggleSelection(nurse.nurse_id);
          }
        }}
      >
        {enableDragHandle && (
          <TableCell className="text-center cursor-grab active:cursor-grabbing">
            <GripVertical className="w-4 h-4 text-gray-400 mx-auto" />
          </TableCell>
        )}
        {/* 체크박스 컬럼 추가 */}
        {enableSelection &&
          (!visibleColumns || visibleColumns.includes("checkbox")) && (
            <TableCell className="font-medium text-center">
              <div className="flex items-center justify-center gap-2">
                {enableSelection && (
                  <button
                    type="button"
                    className="inline-flex items-center justify-center cursor-pointer hover:opacity-70 disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleSelection?.(nurse.nurse_id);
                    }}
                    disabled={isEditMode}
                    aria-label={isSelected ? "선택 해제" : "선택"}
                  >
                    <img
                      src={
                        isSelected
                          ? images.icon_checkbox_fill_gray
                          : images.icon_checkbox_gray
                      }
                      alt=""
                      aria-hidden="true"
                      className="h-[15px] w-[15px]"
                    />
                  </button>
                )}
              </div>
            </TableCell>
          )}
        {(!visibleColumns || visibleColumns.includes("No.")) && (
          <TableCell
            className={cn(
              "font-medium text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isRowMutationPending && (
              <div
                aria-label="Saving"
                className="absolute inset-0 z-[100] flex items-center justify-center bg-white/40"
              >
                <LoadingSpiner size="sm" className="w-auto" />
              </div>
            )}
            <div className="flex items-center justify-center gap-1">
              {showReorder &&
                !isEditMode &&
                !nurse.nurse_id.startsWith("temp_") && (
                  <div className="flex flex-col">
                    <button
                      type="button"
                      className="inline-flex h-4 w-5 items-center justify-center rounded-sm text-gray-400 hover:bg-blue-50 hover:text-[#0E59C6] disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                      onClick={(event) => {
                        event.stopPropagation();
                        onMoveUp?.();
                      }}
                      disabled={!canMoveUp}
                      aria-label="위로 이동"
                    >
                      <ChevronUp className="size-3.5" />
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-4 w-5 items-center justify-center rounded-sm text-gray-400 hover:bg-blue-50 hover:text-[#0E59C6] disabled:cursor-not-allowed disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                      onClick={(event) => {
                        event.stopPropagation();
                        onMoveDown?.();
                      }}
                      disabled={!canMoveDown}
                      aria-label="아래로 이동"
                    >
                      <ChevronDown className="size-3.5" />
                    </button>
                  </div>
                )}
              <span>{idx + 1}</span>
            </div>
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("사번")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.nurse_id || "-"}
          </TableCell>
        )}
        {/* 이름 */}
        {(!visibleColumns || visibleColumns.includes("이름")) && (
          <TableCell
            className={cn(
              "cursor-pointer underline-offset-4 decoration-1 hover:underline transition-all text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
            onClick={() => {
              if (selectIdForSideprofile) {
                selectIdForSideprofile(activeForSideProfile, nurse.nurse_id);
              }
            }}
          >
            <div className="flex min-w-0 items-center justify-center gap-1.5">
              <span className="min-w-0 truncate">{nurse.name}</span>
              <MonthlyMembershipBadge nurseId={nurse.nurse_id} />
            </div>
          </TableCell>
        )}
        {addNurseModal && visibleColumns?.includes("소속") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {(nurse as NurseWithRelation & { affiliation?: string })
              .affiliation || "-"}
          </TableCell>
        )}
        {/* role */}
        {(!visibleColumns || visibleColumns.includes("직종")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="role"
                render={({ field }) => (
                  <Select
                    value={field.value ?? ""}
                    onValueChange={field.onChange}
                    disabled={isPending}
                  >
                    <SelectTrigger className="h-[30px]! w-[80px] bg-white text-center">
                      <SelectValue placeholder="직종" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="RN">RN</SelectItem>
                      <SelectItem value="AN">AN</SelectItem>
                      <SelectItem value="etc">etc</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            ) : nurse.role ? (
              <span className="inline-block max-w-[72px] truncate align-middle">
                {nurse.role}
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("직책")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="level_"
                render={({ field }) => (
                  <Input
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    disabled={isPending}
                    placeholder="직급"
                    className="mx-auto h-[30px] w-[82px] bg-white px-2 text-center text-sm"
                  />
                )}
              />
            ) : (nurse as NurseWithRelation & { _level?: string | null })
                .level_ ||
              (nurse as NurseWithRelation & { _level?: string | null })
                ._level ? (
              <span
                className="inline-block max-w-[72px] truncate align-middle"
                title={
                  (nurse as NurseWithRelation & { _level?: string | null })
                    .level_ ??
                  (nurse as NurseWithRelation & { _level?: string | null })
                    ._level ??
                  undefined
                }
              >
                {(nurse as NurseWithRelation & { _level?: string | null })
                  .level_ ??
                  (nurse as NurseWithRelation & { _level?: string | null })
                    ._level}
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {visibleColumns?.includes("성별") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.gender || "-"}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("Grade")) && (
          <TableCell
            className={cn(
              "text-center align-middle",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            <div className="flex items-center justify-center">
              {isEditMode ? (
                <Controller
                  control={control}
                  name="grade"
                  render={({ field }) => (
                    <Select
                      value={
                        field.value != null && field.value !== 0
                          ? String(field.value)
                          : "none"
                      }
                      onValueChange={(value) =>
                        field.onChange(value === "none" ? 0 : Number(value))
                      }
                      disabled={isPending}
                    >
                      <SelectTrigger className="!h-[30px] min-w-[50px] items-center bg-white px-2">
                        <SelectValue placeholder="-" />
                      </SelectTrigger>
                      <SelectContent className="min-w-[60px] max-h-[200px]">
                        <SelectItem value="none">-</SelectItem>
                        {gradeSelectOptions.map((o) => (
                          <SelectItem
                            key={o.value}
                            value={o.value}
                            // className="max-w-[72px]! overflow-hidden!"
                          >
                            <span className="truncate max-w-[72px]! overflow-hidden!">
                              {o.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              ) : nurse.grade && nurse.grade !== 0 ? (
                <span className="truncate max-w-[72px]! overflow-hidden! inline-block">
                  {resolveGradeLabel(
                    nurse.grade,
                    gradeData?.gradeSelectOptions,
                    gradeData?.grade_names ?? undefined
                  )}
                </span>
              ) : (
                <span className="opacity-40">-</span>
              )}
            </div>
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("경력")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <div className="flex items-center justify-center">
                <Controller
                  control={control}
                  name="experience"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min="0"
                      disabled={isPending}
                      className="w-[50px] h-[30px]! px-2 text-center bg-white"
                      value={field.value ?? 0}
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === "" ? 0 : Number(val));
                      }}
                    />
                  )}
                />
                <span className="ml-1 text-xs!">년</span>
              </div>
            ) : (
              formatExperienceYears(nurse.experience)
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("관리자")) && (
          <TableCell className="text-center">
            <div className="flex justify-center items-center">
              <Controller
                control={control}
                name="is_head_nurse"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!isEditMode || isPending}
                  />
                )}
              />
            </div>
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("프리셉터")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isPreceptor ? (
              "프리셉터"
            ) : nurse.preceptor?.name ? (
              <span>{nurse.preceptor.name}</span>
            ) : (
              <span className="opacity-40">없음</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("상호배제 인원")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {exclusionPartnerName ? (
              <span className="block truncate text-sm font-medium text-zinc-700">
                {exclusionPartnerName}
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("주휴")) && (
          <TableCell
            className={cn(
              "text-center overflow-visible",
              isEditMode ? "relative z-10" : "",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="weekly_off_weekday"
                render={({ field }) => (
                  <Select
                    value={field.value != null ? String(field.value) : "none"}
                    onValueChange={(value) =>
                      field.onChange(value === "none" ? null : Number(value))
                    }
                    disabled={isPending}
                  >
                    <SelectTrigger className="h-[30px]!  px-2 bg-white">
                      <SelectValue placeholder="-" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[60px] max-h-[200px]">
                      <SelectItem disabled={isPending} value="none">
                        -
                      </SelectItem>
                      <SelectItem disabled={isPending} value="0">
                        월
                      </SelectItem>
                      <SelectItem disabled={isPending} value="1">
                        화
                      </SelectItem>
                      <SelectItem disabled={isPending} value="2">
                        수
                      </SelectItem>
                      <SelectItem disabled={isPending} value="3">
                        목
                      </SelectItem>
                      <SelectItem disabled={isPending} value="4">
                        금
                      </SelectItem>
                      <SelectItem disabled={isPending} value="5">
                        토
                      </SelectItem>
                      <SelectItem disabled={isPending} value="6">
                        일
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            ) : (
              <div className="text-center">
                {nurse.weekly_off_weekday != null ? (
                  getWeekdayLabel(nurse.weekly_off_weekday as Weekday)
                ) : (
                  <span className="opacity-40">-</span>
                )}
              </div>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("근무형태")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            <div className="w-full flex justify-center items-center">
              {isEditMode ? (
                <ShiftPopover
                  shiftOptions={shiftOptions}
                  fixedShiftOptions={fixedShiftOptions}
                  disabled={isPending}
                />
              ) : (
                <div className="flex justify-center items-center gap-0.5">
                  {nurse.fixed_shift ? (
                    // 고정근무가 있으면 "{name} 고정" 형태로 표시
                    <span
                      className={cn(
                        "text-sm font-medium  text-white p-1.5 px-2 rounded-sm"
                      )}
                      style={{
                        backgroundColor:
                          shiftColorMap.get(nurse.fixed_shift) ?? "#6B7280",
                        color: getReadableShiftCodeTextColor(shiftColorMap.get(nurse.fixed_shift) ?? "#6B7280"),
                      }}
                    >
                      <span className="font-bold">
                        {fixedShiftOptions.find(
                          (o) => o.value === nurse.fixed_shift
                        )?.value ?? nurse.fixed_shift}
                      </span>
                      <span className="ml-1">고정</span>
                    </span>
                  ) : (
                    // allowed_shifts 배열의 값들을 그대로 표시
                    // 빈 배열일 때는 isUseMid에 따라 M 포함 여부 결정
                    (() => {
                      const nightNurseShifts = nurse.allowed_shifts || [];

                      // 빈 배열이면 모든 shiftOptions 표시 (isUseMid에 따라 M 포함 여부 결정)
                      if (nightNurseShifts.length === 0) {
                        const allShifts = shiftOptions.filter((opt) => {
                          // isUseMid가 false면 M 제외
                          if (isUseMid === false && opt.value === "M") {
                            return false;
                          }
                          return true;
                        });

                        if (allShifts.length === 0) {
                          return <span className="text-sm opacity-40">-</span>;
                        }

                        return allShifts.map((opt, i) => {
                          const color =
                            opt.color ?? shiftColorMap.get(opt.value) ?? "#6B7280";
                          return (
                            <span
                              key={i}
                              className="font-bold size-6.5 text-white flex items-center justify-center shrink-0 rounded-[5px] text-xs"
                              style={{
                                backgroundColor: color,
                                color: getReadableShiftCodeTextColor(color),
                              }}
                            >
                              {opt.label}
                            </span>
                          );
                        });
                      }

                      // 배열에 값이 있으면 해당 값들을 그대로 표시
                      const shiftsToDisplay = nightNurseShifts
                        .map((shiftCode) => {
                          // shiftOptions에서 해당 코드 찾기
                          const opt = shiftOptions.find(
                            (o) => o.value === shiftCode
                          );
                          if (!opt) return null;
                          // isUseMid가 false면 M 제외
                          if (isUseMid === false && shiftCode === "M") {
                            return null;
                          }
                          return opt;
                        })
                        .filter(
                          (opt): opt is ShiftOption => opt !== null
                        );

                      if (shiftsToDisplay.length === 0) {
                        return <span className="text-sm opacity-40">-</span>;
                      }

                      return shiftsToDisplay.map((opt, i) => {
                        const color =
                          opt.color ?? shiftColorMap.get(opt.value) ?? "#6B7280";
                        return (
                          <span
                            key={i}
                            className="font-bold size-6.5 text-white flex items-center justify-center shrink-0 rounded-[5px] text-xs"
                            style={{
                              backgroundColor: color,
                              color: getReadableShiftCodeTextColor(color),
                            }}
                          >
                            {opt.label}
                          </span>
                        );
                      });
                    })()
                  )}
                </div>
              )}
            </div>
          </TableCell>
        )}

        {(!visibleColumns ||
          visibleColumns.includes("나이트 개수") ||
          visibleColumns.includes("나이트 고정")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
            title={
              nightFixedCount != null
                ? `나이트 ${nightFixedCount}개 고정`
                : nightMaxCount != null
                  ? `나이트 ${nightMaxCount}개 이하(최대)`
                  : undefined
            }
          >
            {isEditMode && canEditNight ? (
              <Popover
                open={isNightSettingsOpen}
                onOpenChange={setIsNightSettingsOpen}
              >
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    disabled={isPending}
                    aria-label={`나이트 개수 설정: ${nightSettingLabel}`}
                    className="inline-flex h-8 max-w-full items-center gap-1 rounded-md border border-border bg-card px-2 text-xs font-semibold text-foreground transition-colors hover:bg-light-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sig/30 disabled:opacity-50"
                  >
                    <span className="truncate tabular-nums">
                      {nightMode === "fixed" && nightDraft != null
                        ? `${nightDraft}개`
                        : nightMode === "max" && nightDraft != null
                          ? `≤${nightDraft}개`
                          : "설정"}
                    </span>
                    <PencilLine aria-hidden="true" className="size-3 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  className="w-[272px] border-border bg-card p-3 font-pretendard text-foreground shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
                >
                  <p className="break-keep text-sm font-semibold">
                    {nurse.name
                      ? `${nurse.name} 나이트 개수 설정`
                      : "나이트 개수 설정"}
                  </p>
                  <p className="mt-1 break-keep text-xs leading-5 text-muted-foreground">
                    변경 사항은 이 행의 ‘저장’ 버튼을 눌러야 반영됩니다.
                  </p>
                  <div className="mt-3 grid grid-cols-[132px_minmax(0,1fr)] items-end gap-2">
                    <label className="grid gap-1.5 text-xs font-semibold text-foreground">
                      설정 방식
                      <Select
                        value={nightMode}
                        onValueChange={(value) => {
                          if (value === "fixed" || value === "max") {
                            setNightMode(value);
                            return;
                          }
                          if (value === "none") {
                            setNightMode(value);
                            setNightDraft(null);
                          }
                        }}
                        disabled={isPending}
                      >
                        <SelectTrigger className="h-9 w-full bg-card text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">고정 횟수</SelectItem>
                          <SelectItem value="max">최대 횟수</SelectItem>
                          <SelectItem value="none">설정 안 함</SelectItem>
                        </SelectContent>
                      </Select>
                    </label>
                    <label className="grid gap-1.5 text-xs font-semibold text-foreground">
                      횟수
                      <div className="flex min-w-0 items-center gap-1">
                        <Input
                          type="number"
                          min={0}
                          max={NIGHT_LIMIT_MAX}
                          value={nightDraft ?? ""}
                          aria-label="나이트 횟수, 0부터 15까지"
                          placeholder="—"
                          onChange={(e) => {
                            const value = e.target.value.trim();
                            if (value === "") {
                              setNightDraft(null);
                              return;
                            }
                            if (!/^\d+$/.test(value)) return;
                            const count = Number(value);
                            if (count > NIGHT_LIMIT_MAX) {
                              toast.error(
                                `나이트 개수는 ${NIGHT_LIMIT_MAX}개 이하로 설정할 수 있습니다.`
                              );
                              return;
                            }
                            setNightDraft(count);
                          }}
                          disabled={isPending || nightMode === "none"}
                          className="h-9 min-w-0 flex-1 px-1.5 text-center tabular-nums"
                        />
                        <span className="text-xs font-medium text-muted-foreground">
                          개
                        </span>
                      </div>
                    </label>
                  </div>
                </PopoverContent>
              </Popover>
            ) : nightFixedCount != null ? (
              <span className="font-pretendard text-sm">
                {nightFixedCount}개
              </span>
            ) : nightMaxCount != null ? (
              <span
                className="font-pretendard text-sm text-[#0E59C6]"
                title={`최대 ${nightMaxCount}개 이하`}
              >
                ≤{nightMaxCount}개
              </span>
            ) : (
              <span className="opacity-40">-</span>
            )}
          </TableCell>
        )}

        {(!visibleColumns || visibleColumns.includes("주말 휴무")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="is_weekend_off"
                render={({ field }) => (
                  <Select
                    value={field.value ? "true" : "false"}
                    onValueChange={(val) => field.onChange(val === "true")}
                    disabled={isPending}
                  >
                    <SelectTrigger className="h-[30px]! w-[80px] px-2 bg-white mx-auto">
                      <SelectValue placeholder="적용" />
                    </SelectTrigger>
                    <SelectContent className="min-w-[60px]">
                      <SelectItem value="true">적용</SelectItem>
                      <SelectItem value="false">미적용</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            ) : (
              <div className="text-center">
                {nurse.is_weekend_off ? (
                  "적용"
                ) : (
                  <span className="text-sm opacity-40">미적용</span>
                )}
              </div>
            )}
          </TableCell>
        )}
        {/* {(!visibleColumns || visibleColumns.includes("원티드 요청 제한")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <div className="flex items-center justify-center">
                <Controller
                  control={control}
                  name="wanted_max_requests"
                  render={({ field }) => (
                    <Input
                      type="number"
                      min="0"
                      disabled={isPending}
                      className="w-[50px] h-[30px]! px-2 text-center bg-white"
                      value={field.value ?? ""}
                      placeholder="-"
                      onChange={(e) => {
                        const val = e.target.value;
                        field.onChange(val === "" ? null : Number(val));
                      }}
                    />
                  )}
                />
              </div>
            ) : (
              <>{nurse.wanted_max_requests ?? "-"}</>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("원티드 AI기능 사용")) && (
          <TableCell className="text-center">
            <div className="flex justify-center items-center">
              <Controller
                control={control}
                name="enable_aide"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                      if (!checked) {
                        setValue("enable_nurse_pair_preference", false, {
                          shouldDirty: true,
                        });
                      }
                    }}
                    disabled={!isEditMode || isPending}
                  />
                )}
              />
            </div>
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("시크릿기능 사용")) && (
          <TableCell className="text-center">
            <div className="flex justify-center items-center">
              <Controller
                control={control}
                name="enable_nurse_pair_preference"
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!isEditMode || isPending || !enableAide}
                  />
                )}
              />
            </div>
          </TableCell>
        )} */}
        {(!visibleColumns || visibleColumns.includes("입사일")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="joining_date"
                render={({ field }) => (
                  <StyledDateInput
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder="-"
                    disabled={isPending}
                  />
                )}
              />
            ) : (
              <>
                {nurse.joining_date ? (
                  DateUtils.dateFormatKR(nurse.joining_date, "YYYY-MM-DD")
                ) : (
                  <span className="opacity-40">-</span>
                )}
              </>
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("재직기간")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {DateUtils.getTenureString(
              nurse.joining_date,
              nurse.resignation_date
            )}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("근무 종료일")) && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {isEditMode ? (
              <Controller
                control={control}
                name="resignation_date"
                render={({ field }) => (
                  <StyledDateInput
                    value={field.value ?? null}
                    onChange={field.onChange}
                    placeholder="-"
                    min={watch("joining_date") || undefined}
                    disabled={!watch("joining_date") || isPending}
                  />
                )}
              />
            ) : (
              <>
                {nurse.resignation_date ? (
                  DateUtils.dateFormatKR(nurse.resignation_date, "YYYY-MM-DD")
                ) : (
                  <span className="opacity-40">-</span>
                )}
              </>
            )}
          </TableCell>
        )}
        {visibleColumns?.includes("생년월일") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {formattedBirthDate}
          </TableCell>
        )}
        {visibleColumns?.includes("연락처") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {nurse.phone_number || "-"}
          </TableCell>
        )}
        {!addNurseModal && visibleColumns?.includes("소속") && (
          <TableCell
            className={cn(
              "text-center",
              isSelected && enableSelection && "text-[#D6D6DE]"
            )}
          >
            {(nurse as NurseWithRelation & { affiliation?: string })
              .affiliation || "-"}
          </TableCell>
        )}
        {(!visibleColumns || visibleColumns.includes("메모")) && (
          <TableCell
            className={cn(isSelected && enableSelection && "text-[#D6D6DE]")}
          >
            <Controller
              control={control}
              name="nurse_memo"
              render={({ field }) => (
                <MemoCell
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  isEditMode={isEditMode}
                  disabled={isPending}
                  onlyView={isSelected && enableSelection}
                />
              )}
            />
          </TableCell>
        )}
        {!hideEditColumn &&
          (!visibleColumns || visibleColumns.includes("관리")) && (
          <TableCell className="text-center">
            <div className="flex w-full items-center justify-center gap-1">
              {isEditMode ? (
                <button
                  type="button"
                  className={`p-1 rounded hover:bg-blue-100 transition-colors ${isPending ? "opacity-50 cursor-not-allowed" : ""}`}
                  onClick={handleSubmit(onSubmit, onError)}
                  disabled={isPending}
                  title="저장"
                >
                  저장
                </button>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span>
                        <button
                          type="button"
                          className="rounded p-1 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed"
                          disabled={
                            disableEdit ||
                            isDisabledNurse ||
                            isExternalNurseInCurrentGroup
                          }
                          aria-label={`${nurse.name} 수정`}
                          onClick={() => setIsEditMode(true)}
                        >
                          <PencilLine
                            className="size-4 text-[#D6D6DE] transition-colors hover:text-[#0E59C6]"
                          />
                        </button>
                      </span>
                    </TooltipTrigger>
                    {(disableEdit ||
                      isDisabledNurse ||
                      isExternalNurseInCurrentGroup) && (
                      <TooltipContent>
                        <p>
                          {disableEdit
                            ? "정렬 모드에서는 수정이 불가합니다"
                            : isExternalNurseInCurrentGroup
                              ? "파견 근무자는 사이드프로필에서만 수정이 가능합니다"
                              : "파트장 정보는 수정할 수 없습니다"}
                        </p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              {!isDisabledNurse && (
                <button
                  type="button"
                  className="rounded p-1 transition-colors hover:bg-blue-100 disabled:cursor-not-allowed"
                  onClick={onDeleteHandler}
                  disabled={isPending}
                  title="삭제"
                  aria-label={`${nurse.name} 삭제`}
                >
                  <Trash2 className="size-4 text-[#D6D6DE] transition-colors hover:text-[#0E59C6]" />
                </button>
              )}
            </div>
          </TableCell>
        )}
      </TableRow>
      {isDeleteModalOpen && (
        <DeleteConfirmModal
          isOpen={isDeleteModalOpen}
          memberName={nurse.name}
          groupName={groupName}
          onConfirm={() => {
            deleteNurseMutation(nurse.nurse_id);
          }}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      )}

      <CustomModal.Alert
        isOpen={isNightWeekendBlockedModalOpen}
        onClose={() => setIsNightWeekendBlockeModalOpen(false)}
        title="나이트 전담은 주말 휴무를 적용할 수 없습니다."
      />
    </FormProvider>
  );
}

export const NurseTableRow = memo(NurseTableRowComponent);
